// Typed content for the marketing site. Static, build-time data —
// no API, no CMS. Update here, redeploy.

import type { ComponentType } from "react";
import {
  EurUsdLogo,
  GbpUsdLogo,
  UsdJpyLogo,
  XauLogo,
  XagLogo,
  Us100Logo,
  Spx500Logo,
  BtcLogo,
  EthLogo,
  XrpLogo,
  WtiLogo,
  BrentLogo,
} from "@/components/common/tickers";
import { env } from "@/lib/env";
import { formatDays, formatPercent, formatUsd } from "@/lib/format";
import type { Locale } from "@/i18n/locales";
import type {
  AssetCategoryKey,
  CompareRowKey,
  FaqKey,
  HeroStatKey,
  ChannelKey,
  InstrumentCountKey,
  PlatformFaqKey,
  RuleKey,
  StepKey,
} from "@/content/types";

// Hero stats — a discriminated union so a cell can either animate
// a numeric value via CountUp ("kind: number") or render a static
// text display ("kind: text") for things we don't have measurable
// data for yet (e.g. payout cadence). Keeps the render path in
// Hero.tsx honest — no zero-CountUp tricks, no fake numbers.
// The figures and their formatting stay here; the words moved to
// `src/content/<locale>/landing-hero.ts` and are joined back by `key`. Keying by
// identity rather than array position means reordering this list cannot silently
// attach a label to the wrong number.
export type HeroStat =
  | { key: HeroStatKey; kind: "number"; value: number; format: (n: number, locale: Locale) => string }
  | { key: HeroStatKey; kind: "text" };

export const HERO_STATS: readonly HeroStat[] = [
  { key: "fastPayouts", kind: "text" },
  { key: "profitSplitBase", kind: "number", value: 80, format: formatPercent },
  { key: "payoutCycleDays", kind: "number", value: 7, format: formatDays },
];

export interface TickerItem {
  symbol: string;
  category: string;
  price: string;
  change: string;
  up: boolean;
}

export const HERO_TICKER: readonly TickerItem[] = [
  { symbol: "EUR/USD", category: "FX", price: "1.0843", change: "+0.12%", up: true },
  { symbol: "GBP/USD", category: "FX", price: "1.2691", change: "+0.34%", up: true },
  { symbol: "XAU/USD", category: "Metals", price: "2,318.40", change: "+1.08%", up: true },
  { symbol: "BTC/USD", category: "Crypto", price: "67,420", change: "−0.42%", up: false },
  { symbol: "US100", category: "Indices", price: "18,914.7", change: "+0.61%", up: true },
  { symbol: "USD/JPY", category: "FX", price: "154.82", change: "−0.18%", up: false },
  { symbol: "SPX500", category: "Indices", price: "5,142.40", change: "+0.42%", up: true },
  { symbol: "WTI", category: "Energy", price: "78.91", change: "−0.26%", up: false },
  { symbol: "ETH/USD", category: "Crypto", price: "3,184", change: "+0.88%", up: true },
];

// The glyph is a typographic symbol, not copy; the words moved to
// `src/content/<locale>/landing-offer.ts` and rejoin by `key`.
export interface ComparisonRow {
  key: CompareRowKey;
  glyph: string; // monospace category symbol shown next to the row code
}

export const COMPARE_ROWS: readonly ComparisonRow[] = [
  { key: "drawdownRules", glyph: "⌖" },
  { key: "profitSplits", glyph: "%" },
  { key: "payoutSpeed", glyph: "↓" },
  { key: "ruleClarity", glyph: "§" },
  { key: "accountScaling", glyph: "↑" },
];

export interface AccountTier {
  size: number;
  fee1Step: number;
  fee2Step: number;
  splitPct: number;
  target1Step: number;
  target2Step: number;
  ddDaily: number;
  ddMax: number;
}

// Profit split is a flat 80% on every account size. Traders can
// upgrade to 90% via an optional add-on at checkout — that's the
// single split-bump lever, replacing the earlier per-tier scaling.
export const ACCOUNT_TIERS: readonly AccountTier[] = [
  { size: 10_000, fee1Step: 119, fee2Step: 79, splitPct: 80, target1Step: 1_000, target2Step: 800, ddDaily: 400, ddMax: 700 },
  { size: 25_000, fee1Step: 349, fee2Step: 239, splitPct: 80, target1Step: 2_500, target2Step: 2_000, ddDaily: 1_000, ddMax: 1_750 },
  { size: 50_000, fee1Step: 479, fee2Step: 339, splitPct: 80, target1Step: 5_000, target2Step: 4_000, ddDaily: 2_000, ddMax: 3_500 },
  { size: 100_000, fee1Step: 709, fee2Step: 499, splitPct: 80, target1Step: 10_000, target2Step: 8_000, ddDaily: 4_000, ddMax: 7_000 },
  { size: 200_000, fee1Step: 1_529, fee2Step: 1_069, splitPct: 80, target1Step: 20_000, target2Step: 16_000, ddDaily: 8_000, ddMax: 14_000 },
];


// Kept as a named export for call-site legibility, but it now REQUIRES a locale
// — the old signature silently followed the browser. Making the parameter
// mandatory is what turns every un-migrated call site into a compile error
// rather than a wrong number.
export const fmtUSD = (n: number, locale: Locale): string => formatUsd(n, locale);

// Asset strip — five numbered category cards on the landing page
// The instrument COUNT is a number here so no translation can move it; the
// unit word and the category name live in content.
export interface AssetCategoryCard {
  key: AssetCategoryKey;
  num: string;
  count: number;
  leaderSymbol: string;
  leaderSeries: readonly number[];
  up: boolean;
}

export const ASSET_CATEGORY_CARDS: readonly AssetCategoryCard[] = [
  { key: "forex", num: "01", count: 62, leaderSymbol: "EUR/USD", leaderSeries: [15, 13, 16, 11, 12, 7, 9, 4, 6], up: true },
  { key: "indices", num: "02", count: 24, leaderSymbol: "US100", leaderSeries: [16, 14, 17, 12, 13, 8, 10, 5, 7], up: true },
  { key: "metals", num: "03", count: 8, leaderSymbol: "XAU/USD", leaderSeries: [14, 12, 15, 10, 11, 6, 8, 3, 5], up: true },
  { key: "crypto", num: "04", count: 42, leaderSymbol: "BTC/USD", leaderSeries: [6, 8, 5, 10, 9, 14, 12, 17, 16], up: false },
  { key: "energies", num: "05", count: 12, leaderSymbol: "WTI", leaderSeries: [6, 8, 5, 10, 9, 14, 12, 17, 16], up: false },
];

export interface Step {
  key: StepKey;
  n: string;
}

export const STEPS: readonly Step[] = [
  { key: "chooseAccount", n: "01" },
  { key: "passEvaluation", n: "02" },
  { key: "getFunded", n: "03" },
  { key: "withdrawShare", n: "04" },
];

export interface TimelineStep {
  n: string;
  label: string;
  tip: string;
}

export const TIMELINE_STEPS: readonly TimelineStep[] = [
  { n: "01", label: "SIGN UP", tip: "Pick model + size" },
  { n: "02", label: "EVALUATE", tip: "Hit 8% target" },
  { n: "03", label: "GET FUNDED", tip: "Live capital deployed" },
  { n: "04", label: "WITHDRAW", tip: "Weekly · <24h" },
];

// Consolidated rules table for /how-it-works (1-Step alongside 2-Step,
// each rule with a relative-magnitude bar)
// A rule cell is either a FIGURE (a risk limit — locked here, unreachable from
// any locale module), a figure with an "up to" qualifier, or one of exactly two
// WORDS whose translations live in content. There is no free-text cell, which is
// what makes it impossible for a translation to move a limit.
export type RuleValue =
  | { kind: "figure"; text: string }
  | { kind: "upTo"; figure: string }
  | { kind: "word"; word: "none" | "allowed" };

export interface RulesTableRow {
  key: RuleKey;
  oneStep: RuleValue;
  oneStepFill: number;
  twoStep: RuleValue;
  twoStepFill: number;
}

const figure = (text: string): RuleValue => ({ kind: "figure", text });

export const RULES_TABLE: readonly RulesTableRow[] = [
  { key: "profitTarget", oneStep: figure("10%"), oneStepFill: 100, twoStep: figure("8% + 5%"), twoStepFill: 80 },
  { key: "dailyLoss", oneStep: figure("4%"), oneStepFill: 40, twoStep: figure("5%"), twoStepFill: 50 },
  { key: "maxDrawdown", oneStep: figure("7%"), oneStepFill: 70, twoStep: figure("10%"), twoStepFill: 100 },
  { key: "minTradingDays", oneStep: figure("5"), oneStepFill: 35, twoStep: figure("5"), twoStepFill: 35 },
  { key: "profitSplit", oneStep: { kind: "upTo", figure: "90%" }, oneStepFill: 90, twoStep: { kind: "upTo", figure: "90%" }, twoStepFill: 90 },
  { key: "timeLimit", oneStep: { kind: "word", word: "none" }, oneStepFill: 100, twoStep: { kind: "word", word: "none" }, twoStepFill: 100 },
  { key: "weekendHolding", oneStep: { kind: "word", word: "allowed" }, oneStepFill: 100, twoStep: { kind: "word", word: "allowed" }, twoStepFill: 100 },
];

// Only the key and the platform slug live here. The question, answer and
// category label are copy and moved to `src/content/<locale>/how-it-works.ts`.
export interface FaqItem {
  key: FaqKey;
  // Path under the platform FAQ base (`env.traderFaqUrl`) of the canonical
  // answer on `trade.flex-fund.com`. Format `<category-slug>/<article-slug>`.
  // NEVER localised — translating it 404s the deep link.
  path: string;
}

export const FAQ_ITEMS: readonly FaqItem[] = [
  { key: "timeLimit", path: "evaluation-overview/is-there-a-time-limit-to-complete-the-evaluation" },
  { key: "payoutFrequency", path: "payouts/how-do-payouts-work" },
  { key: "profitSplit", path: "payouts/what-is-the-profit-split" },
  { key: "oneVsTwoStep", path: "evaluation-overview/how-many-phases-are-included-in-the-evaluation" },
  { key: "easCopyTrading", path: "trading-rules-restrictions/can-i-use-expert-advisors-eas-or-bots" },
  { key: "ruleBreach", path: "trading-rules-restrictions/what-happens-if-i-violate-a-trading-rule" },
];

export interface PlatformFaqItem {
  key: PlatformFaqKey;
  /** Platform FAQ slug. Never localised — translating it 404s the deep link. */
  path: string;
}

export const PLATFORM_FAQ_ITEMS: readonly PlatformFaqItem[] = [
  { key: "whichPlatforms", path: "markets-platforms/which-trading-platforms-does-avant-funded-offer" },
  { key: "liquidityExecution", path: "markets-platforms/who-provides-liquidity-and-execution" },
  { key: "leverage", path: "trading-rules-restrictions/what-leverage-is-provided" },
  { key: "easBots", path: "trading-rules-restrictions/can-i-use-expert-advisors-eas-or-bots" },
];

// Only the key and the destination live here; the name, description, CTA and
// reply-time pill are copy and moved to `src/content/<locale>/support.ts`.
export interface Channel {
  key: ChannelKey;
  ctaHref: string;
}

export const CHANNELS: readonly Channel[] = [
  { key: "email", ctaHref: "mailto:support@flex-fund.com" },
  { key: "helpCenter", ctaHref: env.traderFaqUrl },
];

export const SUPPORT_EMAIL = "support@flex-fund.com";

// Phase RW.14 (2026-05-08): TESTIMONIALS, TRUSTPILOT_SCORE, and
// the Testimonial interface were retired alongside the Testimonials
// section component. The platform is pre-launch / first cohort —
// real reviews and real payout receipts don't exist yet, and
// fabricated social proof doesn't belong on a marketing site.
// The Landing page now surfaces operational substance
// (`Infrastructure.tsx`) in the same slot until real testimonials
// can be sourced. Restore this data when first-cohort traders
// publish real numbers.

// Tradable Assets (page) — flat instrument table for the redesign.
// `Logo` is `ComponentType` so the consumer can render `<row.Logo />`
// without forcing every logo component to declare a no-arg
// signature — strict TS treats `(props) => Element` as not
// assignable to `() => Element` even when every prop is optional.
export interface InstrumentRow {
  symbol: string;
  category: "FOREX" | "INDICES" | "METALS" | "CRYPTO" | "ENERGIES" | "STOCKS";
  bid: string;
  spread: string;
  changePct: string;
  up: boolean;
  trend: readonly number[]; // 9-pt sparkline
  leverage: string;
  status: "LIVE" | "RESTRICTED";
  Logo: ComponentType;
}

const UP_TREND: readonly number[] = [16, 14, 17, 12, 13, 8, 10, 5, 6];
const DOWN_TREND: readonly number[] = [6, 8, 5, 10, 9, 14, 12, 17, 16];

export const INSTRUMENT_TABLE: readonly InstrumentRow[] = [
  { symbol: "EUR/USD", category: "FOREX",   bid: "1.08431",  spread: "0.1 pip",  changePct: "+0.12%", up: true,  trend: UP_TREND,   leverage: "1:100", status: "LIVE",       Logo: EurUsdLogo },
  { symbol: "GBP/USD", category: "FOREX",   bid: "1.26910",  spread: "0.3 pip",  changePct: "+0.34%", up: true,  trend: UP_TREND,   leverage: "1:100", status: "LIVE",       Logo: GbpUsdLogo },
  { symbol: "USD/JPY", category: "FOREX",   bid: "154.821",  spread: "0.2 pip",  changePct: "−0.18%", up: false, trend: DOWN_TREND, leverage: "1:100", status: "LIVE",       Logo: UsdJpyLogo },
  { symbol: "XAU/USD", category: "METALS",  bid: "2,318.40", spread: "0.18 pip", changePct: "+1.08%", up: true,  trend: UP_TREND,   leverage: "1:50",  status: "LIVE",       Logo: XauLogo },
  { symbol: "XAG/USD", category: "METALS",  bid: "27.412",   spread: "0.20 pip", changePct: "+0.62%", up: true,  trend: UP_TREND,   leverage: "1:50",  status: "LIVE",       Logo: XagLogo },
  { symbol: "US100",   category: "INDICES", bid: "18,914.7", spread: "0.4 pt",   changePct: "+0.61%", up: true,  trend: UP_TREND,   leverage: "1:50",  status: "LIVE",       Logo: Us100Logo },
  { symbol: "SPX500",  category: "INDICES", bid: "5,142.40", spread: "0.3 pt",   changePct: "+0.42%", up: true,  trend: UP_TREND,   leverage: "1:50",  status: "LIVE",       Logo: Spx500Logo },
  { symbol: "BTC/USD", category: "CRYPTO",  bid: "67,420",   spread: "12.0 USD", changePct: "−0.42%", up: false, trend: DOWN_TREND, leverage: "1:5",   status: "LIVE",       Logo: BtcLogo },
  { symbol: "ETH/USD", category: "CRYPTO",  bid: "3,184",    spread: "0.80 USD", changePct: "+0.88%", up: true,  trend: UP_TREND,   leverage: "1:5",   status: "LIVE",       Logo: EthLogo },
  { symbol: "WTI",     category: "ENERGIES", bid: "78.91",   spread: "0.04 pt",  changePct: "−0.26%", up: false, trend: DOWN_TREND, leverage: "1:20",  status: "LIVE",       Logo: WtiLogo },
  { symbol: "BRENT",   category: "ENERGIES", bid: "82.18",   spread: "0.05 pt",  changePct: "−0.18%", up: false, trend: DOWN_TREND, leverage: "1:20",  status: "LIVE",       Logo: BrentLogo },
  { symbol: "XRP/USD", category: "CRYPTO",  bid: "0.5240",   spread: "0.0008",   changePct: "+2.18%", up: true,  trend: UP_TREND,   leverage: "1:5",   status: "RESTRICTED", Logo: XrpLogo },
];

// `Indices & Stock` cell renders "200+" because the stocks count
// is itself open-ended ("180+ US equities") — adding a fixed
// indices count to it would imply false precision.
export const INSTRUMENT_COUNTS: ReadonlyArray<readonly [key: InstrumentCountKey, count: string]> = [
  ["forexPairs", "62"],
  ["commodities", "20"],
  ["crypto", "42"],
  ["indicesStock", "200+"],
];

// Chip ids are the merged-filter set; per-row `category` tags stay
// granular (FOREX / METALS / INDICES / CRYPTO / ENERGIES / STOCKS)
// so the row badge can render at finer granularity than the chip
// used to filter it.
export type AssetCategoryFilter =
  | "ALL"
  | "FOREX"
  | "COMMODITIES"
  | "CRYPTO"
  | "INDICES_STOCK";

// Ids only. The labels are copy and live in the locale content, keyed by these
// same ids — so translating a chip cannot change what it filters.
export const ASSET_CATEGORY_CHIPS: ReadonlyArray<AssetCategoryFilter> = [
  "ALL",
  "FOREX",
  "COMMODITIES",
  "CRYPTO",
  "INDICES_STOCK",
];

export const CATEGORY_GROUP_MEMBERS: Record<
  AssetCategoryFilter,
  ReadonlyArray<InstrumentRow["category"]>
> = {
  ALL:           ["FOREX", "INDICES", "METALS", "CRYPTO", "ENERGIES", "STOCKS"],
  FOREX:         ["FOREX"],
  COMMODITIES:   ["METALS", "ENERGIES"],
  CRYPTO:        ["CRYPTO"],
  INDICES_STOCK: ["INDICES", "STOCKS"],
};


